import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useDepartmentStore } from "../../store/useDepartmentStore";
import { WidgetContextRegistry } from "./WidgetContextRegistry";
import { WidgetContextOverlay } from "./WidgetContextOverlay";
import type { WidgetContext } from "./types";
import { isStepReady } from "./types";

const PickModeContext = createContext(false);
const PickModeToggleContext = createContext<() => void>(() => {});
const PickModeAvailableContext = createContext(false);

export function usePickMode() {
  return useContext(PickModeContext);
}

export function useTogglePickMode() {
  return useContext(PickModeToggleContext);
}

/** True only when rendered inside WidgetContextInspectorProvider (i.e. context picking is supported). */
export function usePickModeAvailable() {
  return useContext(PickModeAvailableContext);
}

export function WidgetContextInspectorProvider({ children }: { children: React.ReactNode }) {
  const [pickMode, setPickMode] = useState(false);
  const currentStepIndex = useDepartmentStore((s) => s.currentStepIndex);
  const stepStatuses = useDepartmentStore((s) => s.stepStatuses);
  const attachWidgetContext = useDepartmentStore((s) => s.attachWidgetContext);

  const togglePickMode = useCallback(() => {
    setPickMode((prev) => {
      if (prev) {
        document.body.classList.remove("widget-pick-mode");
        return false;
      }
      document.body.classList.add("widget-pick-mode");
      return true;
    });
  }, []);

  useEffect(() => {
    return () => {
      document.body.classList.remove("widget-pick-mode");
    };
  }, []);

  // Exit pick mode on Escape
  useEffect(() => {
    if (!pickMode) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setPickMode(false);
        document.body.classList.remove("widget-pick-mode");
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [pickMode]);

  // In pick mode, add tabindex + aria-label to extractable elements for keyboard a11y
  useEffect(() => {
    if (!pickMode) return;

    const extractors = WidgetContextRegistry.getAll();
    const modified: HTMLElement[] = [];
    for (const [contextId, config] of extractors) {
      const el = document.querySelector(`[data-context-id="${contextId}"]`) as HTMLElement | null;
      if (!el) continue;
      if (!isStepReady(config.type, stepStatuses)) continue;
      el.setAttribute("tabindex", "0");
      el.setAttribute("aria-label", `Extract ${config.label} context for agent`);
      modified.push(el);
    }

    return () => {
      for (const el of modified) {
        el.removeAttribute("tabindex");
        el.removeAttribute("aria-label");
      }
    };
  }, [pickMode, stepStatuses]);

  // Handle click or keyboard activation during pick mode
  const handleExtract = useCallback(
    (target: HTMLElement) => {
      const contextId = target.getAttribute("data-context-id");
      if (!contextId) return;

      const contextType = target.getAttribute("data-context-type") || "";
      if (!isStepReady(contextType, stepStatuses)) return;

      const extractor = WidgetContextRegistry.lookup(contextId);
      if (!extractor) return;

      const extracted = extractor.extract();
      const widgetContext: WidgetContext = {
        type: extracted.type,
        capturedAt: Date.now(),
        widgetId: contextId,
        stepIndex: currentStepIndex,
        label: extractor.label,
        data: extracted.data,
      };

      attachWidgetContext(widgetContext);
      setPickMode(false);
      document.body.classList.remove("widget-pick-mode");
      extractor.onExtracted?.();
    },
    [currentStepIndex, stepStatuses, attachWidgetContext],
  );

  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (!pickMode) return;

      const target = (e.target as HTMLElement).closest("[data-context-id]") as HTMLElement | null;
      if (!target) return;

      e.preventDefault();
      e.stopPropagation();
      handleExtract(target);
    },
    [pickMode, handleExtract],
  );

  // Keyboard: Enter/Space activates extraction on focused extractable element
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!pickMode) return;
      if (e.key !== "Enter" && e.key !== " ") return;

      const target = (e.target as HTMLElement).closest("[data-context-id]") as HTMLElement | null;
      if (!target) return;

      e.preventDefault();
      e.stopPropagation();
      handleExtract(target);
    },
    [pickMode, handleExtract],
  );

  useEffect(() => {
    if (!pickMode) return;
    document.addEventListener("click", handleClick, true);
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [pickMode, handleClick, handleKeyDown]);

  return (
    <PickModeAvailableContext.Provider value={true}>
      <PickModeContext.Provider value={pickMode}>
        <PickModeToggleContext.Provider value={togglePickMode}>
          <WidgetContextOverlay active={pickMode} />
          {children}
        </PickModeToggleContext.Provider>
      </PickModeContext.Provider>
    </PickModeAvailableContext.Provider>
  );
}
